/**
 * FEATURE C2 · 2026-05-21 — Offline indicator pill no TopBar.
 *
 * Aparece quando:
 *   navigator.onLine === false  OU  outboxCount > 0
 *
 * Estados visuais:
 *   - offline + 0 pending: pill cinza "Offline"
 *   - offline + N pending: pill vermelha "Offline · N pendentes"
 *   - online + N pending:  pill amarela "Sincronizando…" (com flush manual)
 *
 * Click → popover com lista agrupada por tipo (RDOs, fotos, FVS).
 * Botão "Tentar sincronizar agora" só fica disponível se navigator.onLine.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  countOutbox,
  flushOutbox,
  listOutbox,
  onOutboxChange,
  type OutboxItem,
} from '@/lib/offline-outbox';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

interface BreakdownByType {
  daily_log: number;
  photo: number;
  fvs: number;
  total: number;
}

function computeBreakdown(items: OutboxItem[]): BreakdownByType {
  const b: BreakdownByType = { daily_log: 0, photo: 0, fvs: 0, total: items.length };
  for (const it of items) {
    if (it.type === 'daily_log.create') b.daily_log += 1;
    else if (it.type === 'photo.upload') b.photo += 1;
    else if (it.type === 'fvs.checklist_item_update') b.fvs += 1;
  }
  return b;
}

export function OfflineIndicator() {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [count, setCount] = useState<number>(0);
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    const n = await countOutbox();
    setCount(n);
    if (open) setItems(await listOutbox());
  }, [open]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const unsubscribe = onOutboxChange(() => { void refresh(); });
    void refresh();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      unsubscribe();
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Carrega lista detalhada quando abre.
  useEffect(() => {
    if (open) { void listOutbox().then(setItems); }
  }, [open]);

  const visible = !online || count > 0;
  if (!visible) return null;

  const tone =
    !online && count > 0
      ? 'err'
      : !online
        ? 'muted'
        : 'warn';

  const label =
    !online && count > 0
      ? isEn
        ? `Offline · ${count} pending`
        : `Offline · ${count} pendente${count === 1 ? '' : 's'}`
      : !online
        ? 'Offline'
        : isEn
          ? `Syncing · ${count}`
          : `Sincronizando · ${count}`;

  async function manualFlush() {
    if (!online) return;
    setBusy(true);
    try {
      await flushOutbox();
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const breakdown = computeBreakdown(items);

  return (
    <div className="relative" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        className={cn(
          'pressable inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors',
          tone === 'err' && 'border-err/40 bg-err/10 text-err',
          tone === 'warn' && 'border-warn/40 bg-warn/10 text-warn',
          tone === 'muted' && 'border-line bg-surface-2 text-ink-muted',
        )}
      >
        <span
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            tone === 'err' && 'bg-err',
            tone === 'warn' && 'bg-warn animate-pulse',
            tone === 'muted' && 'bg-ink-subtle',
          )}
        />
        <span className="hidden sm:inline">{label}</span>
        <span className="sm:hidden">{count > 0 ? count : ''}</span>
      </button>

      {open && (
        <div
          role="dialog"
          className="absolute right-0 top-full z-50 mt-2 w-[280px] rounded-xl border border-line bg-surface shadow-pop dark:shadow-pop-dark"
        >
          <div className="border-b border-line px-3 py-2">
            <p className="text-[13px] font-semibold text-ink">
              {online
                ? (isEn ? 'Waiting to send' : 'Aguardando envio')
                : (isEn ? "You're offline" : 'Você está offline')}
            </p>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {online
                ? (isEn ? 'Sending your data to the server.' : 'Mandando seus dados pro servidor.')
                : (isEn
                    ? 'Your actions are saved on your device and will sync when signal returns.'
                    : 'Suas ações ficam salvas no celular e enviam quando voltar o sinal.')}
            </p>
          </div>
          <ul className="divide-y divide-line text-[13px]">
            <BreakdownRow label="RDOs" count={breakdown.daily_log} />
            <BreakdownRow label="Fotos" count={breakdown.photo} />
            <BreakdownRow label="FVS" count={breakdown.fvs} />
          </ul>
          {breakdown.total === 0 && (
            <p className="px-3 py-3 text-[12px] text-ink-muted">
              {isEn
                ? 'Nothing pending. When you create an RDO or take a photo without signal, it appears here.'
                : 'Nada pendente. Quando você cria RDO ou tira foto sem sinal, ela aparece aqui.'}
            </p>
          )}
          {breakdown.total > 0 && (
            <div className="border-t border-line px-3 py-2.5">
              <button
                type="button"
                disabled={!online || busy}
                onClick={manualFlush}
                className="pressable inline-flex min-h-[36px] w-full items-center justify-center rounded-md bg-brand-400 px-3 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy
                  ? (isEn ? 'Syncing…' : 'Sincronizando…')
                  : online
                    ? (isEn ? 'Try syncing now' : 'Tentar sincronizar agora')
                    : (isEn ? 'Waiting for signal…' : 'Aguardando sinal…')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BreakdownRow({ label, count }: { label: string; count: number }) {
  if (count === 0) return null;
  return (
    <li className="flex items-center justify-between px-3 py-2">
      <span className="text-ink">{label}</span>
      <span className="font-semibold tabular-nums text-ink">{count}</span>
    </li>
  );
}
